import { useCallback, useEffect, useMemo } from 'react';
import camelCase from 'lodash/camelCase';
import groupBy from 'lodash/groupBy';
import mapKeys from 'lodash/mapKeys';
import merge from 'lodash/merge';
import {
	isExtensionHiddenByAppAccessRules,
	isExtensionVisible,
} from '@atlassian/jira-forge-ui-utils/src/utils/extension/index.tsx';
import {
	useLoadingStatusService,
	type LoadingStatus,
} from '@atlassian/jira-common-services/src/use-service/index.tsx';
import {
	CUSTOM_FIELD_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	getEmptyExtensionsObject,
	SOURCE_GLOBAL_ISSUE_CREATE,
	UI_MODIFICATIONS_MODULE,
} from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type {
	UiModificationsExtension,
	CustomFieldExtension,
	AggExtension,
	DataClassificatedExtensionContexts,
	ExtensionContextsMap,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import type { ExtensionPointModule } from '@atlassian/jira-forge-ui-types/src/common/types/module.tsx';
import { fetchModules } from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules/index.tsx';
import { filterOutDuplicatedExtensions } from '@atlassian/jira-forge-ui-utils/src/utils/filter-out-duplicate-extensions/index.tsx';
import type { ForgeContext } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { CloudId, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { transformExtension } from '@atlassian/jira-forge-custom-field-extension-transformer/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type Data = {
	extensions: {
		jiraCustomField: CustomFieldExtension[];
		jiraCustomFieldType: CustomFieldExtension[];
		jiraUiModifications: UiModificationsExtension[];
	};
	accessNarrowedExtensions: {
		jiraCustomField: CustomFieldExtension[];
		jiraCustomFieldType: CustomFieldExtension[];
		jiraUiModifications: UiModificationsExtension[];
	};
};
const moduleToVariableKey = (module: ExtensionPointModule) => camelCase(module);

export const transformResponseWithAccessNarrowedExtensions = (
	extensions: AggExtension[],
): DataClassificatedExtensionContexts => {
	const groupedExtensions = groupBy(extensions, 'type');
	const groupedExtensionsWithMappedKeys = merge(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		mapKeys(groupedExtensions, (_, key) => moduleToVariableKey(key as ExtensionPointModule)),
		getEmptyExtensionsObject(),
	);
	const filteredOutDuplicatedExtensions = filterOutDuplicatedExtensions(
		groupedExtensionsWithMappedKeys,
	);

	const allowedExtensions = getEmptyExtensionsObject();
	const accessNarrowedExtensions = getEmptyExtensionsObject();

	Object.entries(filteredOutDuplicatedExtensions).forEach(([key, module]) => {
		const allowedExtensionsMap = new Map();
		const accessNarrowedExtensionsMap = new Map();
		module.forEach((moduleItem) => {
			if (isExtensionHiddenByAppAccessRules(moduleItem)) {
				accessNarrowedExtensionsMap.set(moduleItem.id, moduleItem);
			} else {
				allowedExtensionsMap.set(moduleItem.id, moduleItem);
			}
		});
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		allowedExtensions[key as keyof ExtensionContextsMap] = [...allowedExtensionsMap.values()];
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		accessNarrowedExtensions[key as keyof ExtensionContextsMap] = [
			...accessNarrowedExtensionsMap.values(),
		];
	});

	return {
		extensions: allowedExtensions,
		accessNarrowedExtensions,
	};
};

export const fetchForgeModulesV2 = async (
	cloudId: CloudId,
	isAnonymous: boolean,
	projectKey?: ProjectKey,
): Promise<Data> => {
	const extensionsData = await fetchModules({
		cloudId,
		isAnonymous,
		context: {
			projectKey,
		},
		includeHidden: true,
		includeScopes: true,
		types: [CUSTOM_FIELD_MODULE, CUSTOM_FIELD_TYPE_MODULE, UI_MODIFICATIONS_MODULE],
		source: SOURCE_GLOBAL_ISSUE_CREATE,
	});
	const extensionsFlat = Object.values(extensionsData).flat();
	return transformResponseWithAccessNarrowedExtensions(extensionsFlat);
};

export const useFetchForgeModules = (
	extraContext: ForgeContext = {},
): {
	customFields: CustomFieldExtension[];
	filteredOutModules: CustomFieldExtension[];
	issueAdjustmentModules: UiModificationsExtension[];
	error: Error | undefined;
	loadingStatus: LoadingStatus;
} => {
	const { cloudId, isAnonymous } = useTenantContext();

	const request = useCallback(() => {
		return fetchForgeModulesV2(cloudId, isAnonymous, extraContext.projectKey);
	}, [cloudId, isAnonymous, extraContext.projectKey]);

	const { data, error, fetch, loadingStatus } = useLoadingStatusService<Data>(request, {
		loading: true,
	});

	useEffect(() => {
		if (extraContext.projectKey) {
			fetch();
		}
	}, [fetch, extraContext.projectKey]);

	return useMemo(() => {
		const customFields: Array<CustomFieldExtension> = [];
		const issueAdjustmentModules: Array<UiModificationsExtension> = [];
		const filteredOutModules: Array<CustomFieldExtension> = [];

		const modules = data?.extensions;
		const accessNarrowedModules = data?.accessNarrowedExtensions;

		accessNarrowedModules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});
		accessNarrowedModules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});

		modules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			if (isExtensionVisible(module)) {
				if (fg('ditto-fcf-support-new-manifest-on-frontend')) {
					const transformedModule = transformExtension(module);
					if (transformedModule) {
						customFields.push(transformedModule);
					}
				} else {
					customFields.push(module);
				}
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			if (isExtensionVisible(module)) {
				if (fg('ditto-fcf-support-new-manifest-on-frontend')) {
					const transformedModule = transformExtension(module);
					if (transformedModule) {
						customFields.push(transformedModule);
					}
				} else {
					customFields.push(module);
				}
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraUiModifications?.forEach((module: UiModificationsExtension) => {
			if (isExtensionVisible(module)) {
				issueAdjustmentModules.push(module);
			}
		});

		return {
			customFields,
			issueAdjustmentModules,
			filteredOutModules,
			error,
			loadingStatus,
		};
	}, [data, loadingStatus, error]);
};
